import { Box, Stack, TextField, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, EmploymentAndBankDetails } from 'Enum';
import {
  CustomCheckbox,
  CustomRadioGroup,
  FormSection,
  PhoneNumber,
  RadioButtons,
  TextareaCounter,
} from 'Components';
import { useFormContext, useWatch } from 'react-hook-form';
import { IEmploymentAndBankDetails } from 'API';
import { generateError } from 'Regex';
import { useAppSelector, useDictionary } from 'Hooks';
import {
  validateLettersAndNumbersOnly,
  validateLettersOnly,
} from 'Utils/validateLettersAndNumbersOnly';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';
import { FormSectionWrapper } from 'Components/FormSectionWrapper';
import { styleTextField } from 'Theme';
import { useEffect, useState } from 'react';
import { yesNoOptions } from 'Constants';
export const FirstSection = () => {
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
  } = useFormContext<IEmploymentAndBankDetails>();

  const workNameFormName = 'workPlaceDetailsSection.workName';
  const workAddressFormName = 'workPlaceDetailsSection.workAddress';
  const managerNameFormName = 'workPlaceDetailsSection.managerName';
  const jobTitleFormName = 'workPlaceDetailsSection.jobTitle';
  const employmentStatusIdFormName =
    'workPlaceDetailsSection.employmentStatusId';

  const [workName, workAddress, managerName, jobTitle] = getValues([
    workNameFormName,
    workAddressFormName,
    managerNameFormName,
    jobTitleFormName,
  ]);
  const [textAreaJobTitle, setTextAreaJobTitle] = useState<string>('');

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.EMPLOYMENT_AND_BANK_DETAILS
  );

  const [employmentStatuses] = useAppSelector(
    ({
      generalContent: {
        generalContent: { employmentStatuses },
      },
    }) => [employmentStatuses]
  );

  const employmentStatus = useWatch({
    name: employmentStatusIdFormName,
  }) as number;

  const [hasNoLandlinePhone, hasNoManagerPhone, isMinistryOfDefenseEmployee] =
    useWatch({
      name: [
        'workPlaceDetailsSection.hasNoLandlinePhone',
        'workPlaceDetailsSection.hasNoManagerPhone',
        `workPlaceDetailsSection.isMinistryOfDefenseEmployee`,
      ],
    });

  const employmentJobDesc =
    employmentStatus == EmploymentAndBankDetails.haveJob
      ? dictionary.firstSectionFifthInput
      : dictionary.firstSectionFifthInputNoJob;

  useEffect(() => {
    if (hasNoLandlinePhone) {
      setValue('workPlaceDetailsSection.landlinePhoneNum', '');
      clearErrors('workPlaceDetailsSection.landlinePhoneNum');
    }
    if (hasNoManagerPhone) {
      setValue('workPlaceDetailsSection.managerPhoneNum', '');
      clearErrors('workPlaceDetailsSection.managerPhoneNum');
    }
  }, [clearErrors, setValue, hasNoLandlinePhone, hasNoManagerPhone]);

  useEffect(() => {
    if (jobTitle) setTextAreaJobTitle(jobTitle);
  }, [jobTitle]);

  useEffect(() => {
    if (errors.workPlaceDetailsSection?.employmentStatusId)
      window.scroll({ behavior: 'smooth', top: 0 });
  }, [errors.workPlaceDetailsSection]);

  return (
    <FormSectionWrapper>
      <FormSection grouped id={dictionary.firstSectionTitle}>
        {employmentStatuses && (
          <CustomRadioGroup
            id={dictionary.employmentStatusesTitle}
            name={`${employmentStatusIdFormName}` as const}
            options={employmentStatuses.map(({ id, value }) => ({
              value: id.toString(),
              label: value,
            }))}
            callback={(id: number) => {
              if (id == EmploymentAndBankDetails.neverWorked) {
                setValue(
                  `workPlaceDetailsSection.isMinistryOfDefenseEmployee`,
                  true
                );
              }
              if (id == EmploymentAndBankDetails.unemployed) {
                setValue(
                  `workPlaceDetailsSection.isMinistryOfDefenseEmployee`,
                  false
                );
              }
            }}
          />
        )}

        {employmentStatuses &&
          employmentStatus < EmploymentAndBankDetails.neverWorked && (
            <>
              <Typography
                sx={{ fontWeight: 'bold', mt: 2 }}
                variant='body1'
                id={dictionary.firstSectionTitle}
              >
                {employmentStatus == EmploymentAndBankDetails.unemployed
                  ? dictionary.firstSectionTitle2
                  : dictionary.firstSectionTitle}
              </Typography>
              {employmentStatus == EmploymentAndBankDetails.haveJob && (
                <RadioButtons
                  name={'workPlaceDetailsSection.isMinistryOfDefenseEmployee'}
                  id={dictionary.isThereRelativeInMinistryOfDefense}
                  infoText={dictionary.isThereRelativeInMinistryOfDefenseNote}
                  options={yesNoOptions}
                />
              )}
              {((isMinistryOfDefenseEmployee === false &&
                employmentStatus == EmploymentAndBankDetails.haveJob) ||
                employmentStatus == EmploymentAndBankDetails.unemployed) && (
                <>
                  <Typography
                    sx={{
                      p: '16px',
                      m: '12px 0',
                      borderRadius: '12px',
                      bgcolor: '#F2F9FF',
                    }}
                  >
                    {dictionary.confidentialWorkPlace}
                  </Typography>
                  <TextField
                    role={
                      Boolean(errors.workPlaceDetailsSection?.workName)
                        ? 'alert'
                        : undefined
                    }
                    inputProps={{
                      maxLength: '50',
                    }}
                    id={dictionary.firstSectionFirstInput}
                    {...register(workNameFormName)}
                    label={dictionary.firstSectionFirstInput}
                    error={Boolean(errors.workPlaceDetailsSection?.workName)}
                    helperText={
                      Boolean(
                        errors.workPlaceDetailsSection?.workName?.message
                      ) &&
                      generateError(
                        dictionary.firstSectionFirstInput,
                        errors.workPlaceDetailsSection?.workName?.message!
                      )
                    }
                    onChange={(event: any) => validateLettersOnly(event)}
                    sx={styleTextField}
                  />
                  <TextField
                    role={
                      Boolean(errors.workPlaceDetailsSection?.workAddress)
                        ? 'alert'
                        : undefined
                    }
                    inputProps={{
                      maxLength: '50',
                    }}
                    id={dictionary.firstSectionSecondInput}
                    {...register(workAddressFormName)}
                    label={dictionary.firstSectionSecondInput}
                    error={Boolean(errors.workPlaceDetailsSection?.workAddress)}
                    helperText={
                      Boolean(
                        errors.workPlaceDetailsSection?.workAddress?.message
                      ) &&
                      generateError(
                        dictionary.firstSectionSecondInput,
                        errors.workPlaceDetailsSection?.workAddress?.message!
                      )
                    }
                    onChange={(event: any) =>
                      validateLettersAndNumbersOnly(event)
                    }
                    sx={styleTextField}
                  />
                  <TextField
                    role={
                      Boolean(errors.workPlaceDetailsSection?.managerName)
                        ? 'alert'
                        : undefined
                    }
                    inputProps={{
                      maxLength: '50',
                    }}
                    id={dictionary.firstSectionThirdInput}
                    {...register(managerNameFormName)}
                    label={dictionary.firstSectionThirdInput}
                    error={Boolean(errors.workPlaceDetailsSection?.managerName)}
                    helperText={
                      Boolean(
                        errors.workPlaceDetailsSection?.managerName?.message
                      ) &&
                      generateError(
                        dictionary.firstSectionThirdInput,
                        errors.workPlaceDetailsSection?.managerName?.message!
                      )
                    }
                    onChange={(event: any) => validateLettersOnly(event)}
                    sx={styleTextField}
                  />
                  <Stack>
                    <PhoneNumber
                      phoneNumberError={Boolean(
                        errors.workPlaceDetailsSection?.managerPhoneNum
                      )}
                      phoneNumberErrorMsg={
                        errors.workPlaceDetailsSection?.managerPhoneNum?.message
                      }
                      phoneNumberFormName='workPlaceDetailsSection.managerPhoneNum'
                      phoneNumberPrefixFormName='workPlaceDetailsSection.managerPhonePrefixRelationId'
                      phoneNumberLabel={dictionary.firstSectionFourthInput}
                      disabled={hasNoManagerPhone}
                    />
                    <CustomCheckbox
                      name={'workPlaceDetailsSection.hasNoManagerPhone'}
                      label={dictionary.firstSectionNoInfo}
                    />
                  </Stack>
                  <Stack>
                    <PhoneNumber
                      phoneNumberError={Boolean(
                        errors.workPlaceDetailsSection?.landlinePhoneNum
                      )}
                      phoneNumberErrorMsg={
                        errors.workPlaceDetailsSection?.landlinePhoneNum
                          ?.message
                      }
                      phoneNumberFormName='workPlaceDetailsSection.landlinePhoneNum'
                      phoneNumberPrefixFormName='workPlaceDetailsSection.landlinePrefixRelationId'
                      phoneNumberLabel={dictionary.firstSectionTelephoneNumber}
                      disabled={hasNoLandlinePhone}
                    />
                    <CustomCheckbox
                      name={'workPlaceDetailsSection.hasNoLandlinePhone'}
                      label={dictionary.firstSectionNoInfo}
                    />
                  </Stack>
                  <Box
                    sx={{
                      position: 'relative',
                    }}
                  >
                    <TextField
                      role={
                        Boolean(errors.workPlaceDetailsSection?.jobTitle)
                          ? 'alert'
                          : undefined
                      }
                      inputProps={{
                        maxLength: '500',
                      }}
                      id={employmentJobDesc}
                      {...register(jobTitleFormName)}
                      label={employmentJobDesc}
                      multiline
                      rows={4}
                      error={Boolean(errors.workPlaceDetailsSection?.jobTitle)}
                      helperText={
                        Boolean(
                          errors.workPlaceDetailsSection?.jobTitle?.message
                        ) &&
                        generateError(
                          employmentJobDesc,
                          errors.workPlaceDetailsSection?.jobTitle?.message!
                        )
                      }
                      placeholder={dictionary.firstSectionFifthInputPlaceholder}
                      onChange={(event: any) => {
                        setTextAreaJobTitle(event.target.value);
                        validateLettersNumbersAndSpacialChars(event);
                      }}
                    />
                    <TextareaCounter
                      counter={textAreaJobTitle.length}
                      limit={500}
                      hasError={Boolean(
                        errors.workPlaceDetailsSection?.jobTitle
                      )}
                    />
                  </Box>
                </>
              )}
            </>
          )}
      </FormSection>
    </FormSectionWrapper>
  );
};
